import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import Toast from '../../components/Toast';
import { formatTimeAgo } from '../../utils/time';

interface Message {
  id: string;
  message_type: string;
  subject?: string;
  content: string;
  sender_type: string;
  sender_name: string;
  sender_email: string;
  status: string;
  sent_at: string;
  read_at?: string;
  created_at: string;
}

interface ConversationInfo {
  id: string;
  subject: string;
  company_name: string;
  status: string;
  created_at: string;
}

interface ConversationResponse {
  messages: Message[];
  total: number;
  conversation: ConversationInfo;
}

const CandidateConversationDetail: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<Message[]>([]);
  const [conversation, setConversation] = useState<ConversationInfo | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning" | "info";
    isVisible: boolean;
  }>({
    message: "",
    type: "success",
    isVisible: false,
  });

  useEffect(() => {
    if (conversationId) {
      loadConversation();
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversation = async () => {
    if (!conversationId) return;

    try {
      setLoading(true);
      const response: ConversationResponse =
        await apiService.getCandidateConversationMessages(conversationId);
      setMessages(response.messages);
      setConversation(response.conversation);
    } catch (error) {
      console.error("Failed to load conversation:", error);
      setToast({
        message: "Failed to load conversation. Please try again.",
        type: "error",
        isVisible: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendReply = async () => {
    if (!conversationId || !replyContent.trim()) return;

    try {
      setSendingMessage(true);
      await apiService.replyCandidateToConversation(conversationId, {
        content: replyContent,
      });

      setToast({
        message: "Reply sent successfully!",
        type: "success",
        isVisible: true,
      });

      setReplyContent("");
      await loadConversation(); // Reload to get the new message
    } catch (error) {
      console.error("Failed to send reply:", error);
      setToast({
        message: "Failed to send reply. Please try again.",
        type: "error",
        isVisible: true,
      });
    } finally {
      setSendingMessage(false);
    }
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                    <div className="h-16 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="p-6 max-w-4xl mx-auto text-center">
        <div className="text-6xl mb-4">❌</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Conversation not found
        </h2>
        <p className="text-gray-600 mb-6">
          The conversation you're looking for doesn't exist or you don't have
          access to it.
        </p>
        <button
          onClick={() => navigate("/candidate/messages")}
          className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition-colors"
        >
          Back to Messages
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate("/candidate/messages")}
              className="text-emerald-600 hover:text-emerald-700 mb-2 flex items-center"
            >
              ← Back to Messages
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {conversation.subject}
            </h1>
            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
              <span className="flex items-center">
                <span className="mr-1">🏢</span>
                {conversation.company_name}
              </span>
              <span>Started {formatTimeAgo(conversation.created_at)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.sender_type === "job_seeker"
                ? "justify-end"
                : "justify-start"
            }`}
          >
            <div
              className={`max-w-xs lg:max-w-md px-4 py-3 rounded-lg ${
                message.sender_type === "job_seeker"
                  ? "bg-emerald-600 text-white"
                  : "bg-white border border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span
                  className={`text-sm font-medium ${
                    message.sender_type === "job_seeker"
                      ? "text-emerald-100"
                      : "text-gray-700"
                  }`}
                >
                  {message.sender_name}
                </span>
                <span
                  className={`text-xs ${
                    message.sender_type === "job_seeker"
                      ? "text-emerald-200"
                      : "text-gray-500"
                  }`}
                >
                  {formatTimeAgo(message.sent_at)}
                </span>
              </div>

              {message.subject &&
                message.message_type === "initial_contact" && (
                  <div
                    className={`text-sm mb-2 font-medium ${
                      message.sender_type === "job_seeker"
                        ? "text-emerald-100"
                        : "text-gray-600"
                    }`}
                  >
                    Subject: {message.subject}
                  </div>
                )}

              <div
                className={`text-sm whitespace-pre-wrap ${
                  message.sender_type === "job_seeker"
                    ? "text-white"
                    : "text-gray-800"
                }`}
              >
                {message.content}
              </div>

              {message.sender_type === "job_seeker" && (
                <div className="flex items-center justify-end mt-2">
                  <span className="text-xs text-emerald-200">
                    {message.status}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Send Reply</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Your Reply
            </label>
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              rows={4}
              disabled={conversation?.status !== "active"}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder={
                conversation?.status !== "active"
                  ? "Replies are disabled for archived or closed conversations."
                  : "Type your reply here..."
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="flex items-center">
                <span className="mr-1">📧</span>
                The company will be notified via email and in-app notification
              </span>
            </div>

            <button
              onClick={handleSendReply}
              disabled={
                conversation?.status !== "active" ||
                !replyContent.trim() ||
                sendingMessage
              }
              className="bg-emerald-600 text-white px-6 py-2 rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sendingMessage ? "Sending..." : "Send Reply"}
            </button>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={closeToast}
        />
      )}
    </div>
  );
};

export default CandidateConversationDetail;
